import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { getHardcopyAccess, createHardcopyAccessBatch, getStaff, getStudents } from "../../lib/api";
import { formatAccessedAt } from "../students/HardCopyAccessTab";

export default function HardcopyAccessAdmin() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  // Bulk-log dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [students, setStudents] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState({});   // student_id -> true
  const [studentQ, setStudentQ] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    const res = await getHardcopyAccess();
    setLogs(res.data || []);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await reload();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load hardcopy access history");
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, []);

  const openDialog = async () => {
    setStaffId(""); setReason(""); setSelected({}); setStudentQ("");
    setDialogOpen(true);
    try {
      const [s, st] = await Promise.all([getStaff(), getStudents()]);
      setStaff([...(s.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      const list = st.data?.students || st.data || [];
      setStudents([...list].sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "")));
    } catch (e) {
      toast.error("Failed to load staff/students");
    }
  };

  const studentName = (s) => `${(s.preferred_name || s.first_name || "").trim()} ${s.last_name || ""}`.trim();

  const filteredStudents = useMemo(() => {
    const term = studentQ.trim().toLowerCase();
    if (!term) return students;
    return students.filter((s) => studentName(s).toLowerCase().includes(term));
  }, [students, studentQ]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const toggleStudent = (id) => setSelected((m) => ({ ...m, [id]: !m[id] }));
  const selectAllFiltered = () => {
    const next = { ...selected };
    filteredStudents.forEach((s) => { next[s.id] = true; });
    setSelected(next);
  };
  const clearSelection = () => setSelected({});

  const confirm = async () => {
    if (!staffId) { toast.error("Select the staff member who accessed the records"); return; }
    const ids = Object.keys(selected).filter((id) => selected[id]);
    if (ids.length === 0) { toast.error("Select at least one student"); return; }
    setSaving(true);
    try {
      const res = await createHardcopyAccessBatch({ student_ids: ids, staff_id: staffId, reason });
      toast.success(`Logged hardcopy access for ${res.data.created_count} student${res.data.created_count === 1 ? "" : "s"}`);
      setDialogOpen(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log access");
    }
    setSaving(false);
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return logs;
    return logs.filter((l) =>
      (l.student_name || "").toLowerCase().includes(term) ||
      (l.staff_name || "").toLowerCase().includes(term)
    );
  }, [logs, q]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <CardTitle>Hard Copy Access History</CardTitle>
            <CardDescription>Every recorded access of a student's physical record, across all students. Newest first.</CardDescription>
          </div>
          <Button onClick={openDialog} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="bulk-hardcopy-access-btn">
            + Log Access for Multiple Students
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by student or staff name…"
          className="max-w-sm"
          data-testid="hardcopy-admin-search"
        />
        {loading ? (
          <div className="flex items-center justify-center h-24"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#00305E]" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="hardcopy-admin-empty">
            {logs.length === 0 ? "No hardcopy access has been recorded yet." : "No results match your search."}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm" data-testid="hardcopy-admin-table">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Student</th>
                  <th className="px-3 py-2 font-medium">Accessed By</th>
                  <th className="px-3 py-2 font-medium">Date &amp; Time</th>
                  <th className="px-3 py-2 font-medium">Reason</th>
                  <th className="px-3 py-2 font-medium">Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t" data-testid={`hardcopy-admin-row-${l.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/students/${l.student_id}?tab=logs`} className="text-[#00305E] hover:underline">{l.student_name || "—"}</Link>
                    </td>
                    <td className="px-3 py-2">{l.staff_name || "—"}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatAccessedAt(l.accessed_at)}</td>
                    <td className="px-3 py-2 text-gray-700">{l.reason || "—"}</td>
                    <td className="px-3 py-2 text-muted-foreground">{l.recorded_by_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-hardcopy-dialog">
          <DialogHeader>
            <DialogTitle>Log Hard Copy Access — Multiple Students</DialogTitle>
            <DialogDescription>
              Record that one staff member accessed several students' physical records at once.
              The date and time are stamped automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Staff member who accessed the records *</Label>
              <Select value={staffId} onValueChange={setStaffId}>
                <SelectTrigger data-testid="bulk-hardcopy-staff-select"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason / purpose (optional)</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., Annual record audit" data-testid="bulk-hardcopy-reason-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Students ({selectedCount} selected) *</Label>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={selectAllFiltered} data-testid="bulk-hardcopy-select-all">Select shown</Button>
                  <Button type="button" variant="outline" size="sm" onClick={clearSelection} data-testid="bulk-hardcopy-clear">Clear</Button>
                </div>
              </div>
              <Input value={studentQ} onChange={(e) => setStudentQ(e.target.value)} placeholder="Search students…" data-testid="bulk-hardcopy-student-search" />
              <div className="max-h-60 overflow-y-auto rounded-md border divide-y" data-testid="bulk-hardcopy-student-list">
                {filteredStudents.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No students match.</p>
                ) : filteredStudents.map((s) => (
                  <label key={s.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`bulk-hardcopy-student-${s.id}`}>
                    <Checkbox checked={!!selected[s.id]} onCheckedChange={() => toggleStudent(s.id)} data-testid={`bulk-hardcopy-check-${s.id}`} />
                    <span className="text-sm">{studentName(s)}{s.grade ? <span className="text-muted-foreground"> · Grade {s.grade}</span> : null}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={confirm} disabled={saving} data-testid="confirm-bulk-hardcopy-btn">
              {saving ? "Logging…" : `Log Access${selectedCount ? ` (${selectedCount})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
